export const VIGILANCIA_MS = 4000;
export const SONDEO_MS = 250;

export const ESTADO_CAMARA_INICIAL = {
  fase: "inactiva", // inactiva | iniciando | verificando | activa | error
  camaras: [],
  camaraActivaId: null,
  aviso: null,
  error: null,
  detalle: null,
};

const AVISO_FRONTAL_ALTERNATIVA =
  "La cámara trasera no pudo iniciarse; se está usando la cámara frontal como alternativa. Puedes continuar, pero esto no confirma que la cámara trasera funcione.";
const AVISO_FRONTAL_ENTREGADA =
  "Se pidió la cámara trasera, pero el navegador entregó la frontal. Elige una cámara trasera en el selector si la necesitas.";
const AVISO_ALTERNATIVA =
  "La cámara trasera no pudo iniciarse; se usó una cámara alternativa.";
const AVISO_SIN_ID =
  "No se pudo identificar la cámara activa.";
const MSG_SIN_IMAGEN =
  "La cámara se inició pero no envió imágenes. Se liberó la cámara; puedes reintentar o elegir otra.";
const MSG_SE_DETUVO =
  "La cámara dejó de enviar imágenes. Se liberó la cámara; puedes reintentar o elegir otra.";

// Enumeración sin abrir captura (Html5Qrcode.getCameras abre getUserMedia).
export const enumerarCamaras = async () => {
  const md = typeof navigator !== "undefined" ? navigator.mediaDevices : null;
  if (!md || !md.enumerateDevices) return [];
  const lista = await md.enumerateDevices();
  return lista
    .filter((d) => d.kind === "videoinput" && d.deviceId)
    .map((d) => ({ id: d.deviceId, label: d.label || "" }));
};

export const intentosPara = (deviceId) =>
  deviceId
    ? [{ tipo: "elegida", camara: { deviceId: { exact: deviceId } } }]
    : [
        { tipo: "trasera", camara: { facingMode: "environment" } },
        // videoConstraints {} equivale a getUserMedia({ video: true }); la librería ignora `camara`.
        { tipo: "alternativa", camara: { facingMode: "environment" }, videoConstraints: {} },
      ];

export const mensajeError = (err) => {
  const txt = `${err?.name || ""} ${err?.message || ""} ${typeof err === "string" ? err : ""}`;
  if (/NotAllowedError|Permission/i.test(txt))
    return "Permisos de cámara denegados. Habilítalos desde el navegador.";
  if (/NotFoundError|DevicesNotFound/i.test(txt))
    return "No se encontraron cámaras disponibles en este dispositivo.";
  if (/OverconstrainedError/i.test(txt))
    return "La cámara elegida no está disponible. Elige otra o reintenta.";
  if (/NotReadableError|TrackStartError/i.test(txt))
    return "La cámara no pudo iniciarse (puede estar en uso). Reintenta o elige otra.";
  return "No se pudo iniciar la cámara. Reintenta o elige otra.";
};

const esFrontal = (ajustes, etiqueta) =>
  ajustes.facingMode
    ? ajustes.facingMode === "user"
    : /front|frontal|delantera|facetime/i.test(etiqueta || "");

const avisoPara = (tipo, activaId, frontal) => {
  const partes = [];
  if (tipo === "alternativa") partes.push(frontal ? AVISO_FRONTAL_ALTERNATIVA : AVISO_ALTERNATIVA);
  else if (tipo === "trasera" && frontal) partes.push(AVISO_FRONTAL_ENTREGADA);
  if (!activaId) partes.push(AVISO_SIN_ID);
  return partes.length ? partes.join(" ") : null;
};

/**
 * Operaciones de cámara serializadas: cada iniciar/detener recibe una
 * generación; una operación superada no arranca ni deja flujos abiertos.
 */
export const crearControlCamara = ({
  crearEscaner,
  configEscaneo,
  alLeer,
  obtenerVideo,
  enumerar = enumerarCamaras,
  notificar,
  vigilanciaMs = VIGILANCIA_MS,
  sondeoMs = SONDEO_MS,
  ahora = () => Date.now(),
}) => {
  let cola = Promise.resolve();
  let gen = 0;
  let escaner = null;
  let sondeo = null;
  let destruido = false;
  let estado = { ...ESTADO_CAMARA_INICIAL };

  const emitir = (parcial) => {
    estado = { ...estado, ...parcial };
    if (!destruido) notificar(estado);
  };
  const encolar = (op) => {
    const p = cola.then(op);
    cola = p.catch(() => {});
    return p;
  };
  const pararSondeo = () => {
    if (sondeo) clearInterval(sondeo);
    sondeo = null;
  };
  const liberar = async () => {
    pararSondeo();
    const e = escaner;
    escaner = null;
    const video = obtenerVideo();
    if (e) {
      try {
        await e.stop();
      } catch {
        /* no estaba escaneando */
      }
      try {
        e.clear();
      } catch {
        /* ignore */
      }
    }
    const pistas = video?.srcObject?.getTracks?.() || [];
    pistas.forEach((t) => t.stop());
  };

  const fallar = (mio, mensaje, detalle) => {
    pararSondeo();
    return encolar(async () => {
      if (mio !== gen) return;
      await liberar();
      emitir({ fase: "error", camaraActivaId: null, error: mensaje, detalle });
    });
  };

  // Cuenta desde que start() resolvió (permiso ya concedido, video arrancando).
  const vigilar = (mio) => {
    let ultimoT = 0;
    let ultimoProgreso = ahora();
    let recibio = false;
    sondeo = setInterval(() => {
      if (mio !== gen) return pararSondeo();
      const v = obtenerVideo();
      const pista = v?.srcObject?.getVideoTracks?.()[0];
      if (pista && pista.readyState === "ended") {
        return fallar(mio, recibio ? MSG_SE_DETUVO : MSG_SIN_IMAGEN, "pista de video terminada (ended)");
      }
      if (v && v.videoWidth > 0 && v.readyState >= 2 && v.currentTime > ultimoT) {
        ultimoT = v.currentTime;
        ultimoProgreso = ahora();
        if (!recibio) {
          recibio = true;
          emitir({ fase: "activa" });
        }
        return;
      }
      if (ahora() - ultimoProgreso >= vigilanciaMs) {
        const motivo = [
          `sin avance de video en ${vigilanciaMs} ms`,
          v ? `videoWidth=${v.videoWidth} readyState=${v.readyState}` : "sin elemento video",
          pista ? `pista muted=${pista.muted} readyState=${pista.readyState}` : "sin pista",
        ].join("; ");
        fallar(mio, recibio ? MSG_SE_DETUVO : MSG_SIN_IMAGEN, motivo);
      }
    }, sondeoMs);
  };

  const confirmar = async (mio, tipo) => {
    let ajustes = {};
    try {
      ajustes = escaner.getRunningTrackSettings() || {};
    } catch {
      /* sin ajustes */
    }
    let camaras = estado.camaras;
    try {
      camaras = await enumerar();
    } catch {
      /* conserva la lista anterior */
    }
    if (mio !== gen) return liberar();
    const activaId = ajustes.deviceId || null;
    const etiqueta = camaras.find((c) => c.id === activaId)?.label;
    emitir({
      fase: "verificando",
      camaras,
      camaraActivaId: activaId,
      aviso: avisoPara(tipo, activaId, esFrontal(ajustes, etiqueta)),
      error: null,
      detalle: null,
    });
    vigilar(mio);
  };

  return {
    cargarCamaras() {
      return encolar(async () => {
        try {
          const camaras = await enumerar();
          emitir({ camaras });
        } catch {
          /* ignore */
        }
      });
    },
    iniciar(deviceId) {
      const mio = ++gen;
      pararSondeo();
      emitir({ fase: "iniciando", error: null, aviso: null, detalle: null });
      return encolar(async () => {
        if (mio !== gen) return;
        await liberar();
        let ultimoError = null;
        for (const it of intentosPara(deviceId)) {
          if (mio !== gen) return;
          const e = crearEscaner();
          escaner = e;
          // Sólo la instancia vigente de esta generación puede validar lecturas.
          const lectura = (texto) => {
            if (destruido || mio !== gen || escaner !== e) return;
            alLeer(texto);
          };
          const config = it.videoConstraints
            ? { ...configEscaneo, videoConstraints: it.videoConstraints }
            : configEscaneo;
          try {
            await e.start(it.camara, config, lectura, () => {});
          } catch (err) {
            ultimoError = err;
            await liberar();
            continue;
          }
          if (mio !== gen) return liberar();
          return confirmar(mio, it.tipo);
        }
        if (mio !== gen) return;
        emitir({
          fase: "error",
          camaraActivaId: null,
          error: mensajeError(ultimoError),
          detalle: String(ultimoError?.message || ultimoError || ""),
        });
      });
    },
    detener() {
      ++gen;
      pararSondeo();
      return encolar(async () => {
        await liberar();
        emitir({ fase: "inactiva", camaraActivaId: null, aviso: null, error: null, detalle: null });
      });
    },
    destruir() {
      ++gen;
      destruido = true;
      pararSondeo();
      return encolar(liberar);
    },
    estado: () => estado,
  };
};
